// InternalTransfer.jsx — v7
// Changes from v6:
//   · Top-level mode toggle: "Pay" (send) vs "Request" (invoice)
//   · Request path: WHO → AMOUNT → NOTE → CONFIRM (no PAY step, balance-irrelevant)
//   · Request mutation: calls client.balanceLedger.requestTransfer (batch invoice)
//   · Pay path: unchanged from v6
//   · ConfirmScreen receives mode prop so it can render the right copy
//   · RequestConfirmScreen: shows invoice summary + 2FA if totals >= $500

import React, { useState, useEffect } from "react";
import Globe from "./../../../Pages/Dashboard/dashboard/globe";
import {
  Box, Typography, TextField, Button, Paper, Stack, Chip, Avatar,
  CircularProgress, IconButton, Fade, InputAdornment, Divider, Alert,
  Grid, Card, Skeleton, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import {
  Search as SearchIcon, Close as CloseIcon, Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon, TableChart as CSVIcon,
  TrendingUp, TrendingDown, Paid as PaidIcon, AddCircle as AddCircleIcon,
  CardGiftcard as GiftIcon, Upload as UploadIcon,
  Payment as PayPalIcon, CreditCard as CardIcon,
  AccountBalanceWallet as WalletIcon, SwapHoriz as SwapIcon,
  PeopleOutline as PeopleIcon, Email as EmailIcon, Phone as PhoneIcon,
  EditNote as NoteIcon, RequestQuote as RequestIcon, Send as SendIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { alpha, useTheme, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import client from "../../../API";
import useAuth from "../../../Hooks/use-auth";

import { computeT2A, fmtUSD, buildTransferItem, isValidEmail, isValidPhone } from "./../../../Pages/BlitzPay/t2a/T2a.utils";
import { useRecipients }       from "./../../../Pages/BlitzPay//t2a/userrecipients";
import { use2FA }              from "./../../../Pages/BlitzPay/t2a/use2fa";
import { useGlobalUserSearch } from "./../../../Pages/BlitzPay/t2a/useglobalusersearch";
import { CSVUploadModal }      from "./../../../Pages/BlitzPay/t2a/csvuploadmodel";
import { ConfirmScreen }       from "./../../../Pages/BlitzPay/t2a/confirmscreen";
import { RecipientCard }       from "./../../../Pages/BlitzPay/t2a/recipientcard";
import { TransferSummaryBar }  from "./../../../Pages/BlitzPay/t2a/transfersummarybar";
import { useRecipientProfile } from "./../../../Pages/BlitzPay/t2a/userrecipientprofile";

// ─── Tokens ───────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0);   }
`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

function fmtDate(ds) {
  if (!ds) return "";
  const d = new Date(ds), now = new Date();
  const days = Math.floor(Math.abs(now - d) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)  return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function txDisplay(tx) {
  const op = tx.operation_type, change = parseFloat(tx.change_amount || 0), credit = change > 0;
  if (op?.includes("t2a"))        return { icon: <GiftIcon />,   color: credit ? "success" : "warning", title: credit ? "Transfer received" : "Transfer sent", credit };
  if (op?.includes("request"))    return { icon: <RequestIcon />, color: credit ? "success" : "info",   title: credit ? "Request won" : "Request sent",  credit };
  if (op?.includes("commission")) return { icon: <PaidIcon />,   color: "success", title: "Commission", credit: true };
  if (op?.includes("transfer"))   return { icon: credit ? <TrendingUp /> : <TrendingDown />, color: credit ? "success" : "primary", title: tx.reason || tx.brief || "Transfer", credit };
  if (op === "payment")           return { icon: <AddCircleIcon />, color: "success", title: "Deposit", credit: true };
  return { icon: credit ? <TrendingUp /> : <TrendingDown />, color: credit ? "success" : "text.primary", title: tx.reason || tx.brief || (credit ? "Credit" : "Debit"), credit };
}

// ─── Steps ────────────────────────────────────────────────────────────────────
const S = { WHO:"who", AMOUNT:"amount", NOTE:"note", PAY:"pay", CONFIRM:"confirm" };

// ─── Globe Background ─────────────────────────────────────────────────────────
function GlobeBackground({ size = 600 }) {
  const theme = useTheme();
  return (
    <Box aria-hidden="true" sx={{
      pointerEvents:"none", opacity:0.12,
      transform:"rotate3d(1, 0.3, 0, 22deg)",
      maskImage:"radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
      WebkitMaskImage:"radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
    }}>
      <Globe size={size} color={theme.palette.primary.main} backgroundColor="transparent" borderColor="transparent" borderWidth={0} />
    </Box>
  );
}

// ─── Mode Toggle ─────────────────────────────────────────────────────────────
function ModeToggle({ mode, onChange, disabled }) {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 0.5, display: "inline-flex", gap: 0.5, borderColor: "grey.200" }}>
      {[
        { value: "pay",     label: "Transfer", icon: <SendIcon sx={{ fontSize: 15 }} />,    accent: theme.palette.primary.main },
        { value: "request", label: "Request",  icon: <RequestIcon sx={{ fontSize: 15 }} />, accent: theme.palette.success.main },
      ].map((opt) => {
        const active = mode === opt.value;
        return (
          <Button key={opt.value} size="small" disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            startIcon={opt.icon}
            sx={{
              borderRadius: 2.5, px: 2.5, py: 0.75, fontWeight: 700, fontSize: "0.82rem",
              textTransform: "none", minWidth: 100,
              bgcolor: active ? opt.accent : "transparent",
              color:   active ? "white"   : "text.secondary",
              boxShadow: active ? `0 2px 10px ${alpha(opt.accent, 0.35)}` : "none",
              "&:hover": { bgcolor: active ? opt.accent : alpha(opt.accent, 0.07), color: active ? "white" : opt.accent },
              transition: "all 0.18s ease",
            }}>
            {opt.label}
          </Button>
        );
      })}
    </Paper>
  );
}

// ─── Balance Hero ─────────────────────────────────────────────────────────────
function BalanceHero({ balance, loading, onRefresh, mode }) {
  const theme = useTheme();
  const isRequest = mode === "request";
  const accent = isRequest ? theme.palette.success.main : theme.palette.primary.main;
  const accentDark = isRequest ? theme.palette.success.dark : theme.palette.primary.dark;
  const accentLight = isRequest ? theme.palette.success.light : theme.palette.primary.light;

  return (
    <Box sx={{
      mb: 2.5, p: 3, borderRadius: 4,
      background: `linear-gradient(135deg, ${accentDark} 0%, ${accent} 60%, ${alpha(accentLight, 0.8)} 100%)`,
      boxShadow: `0 8px 32px ${alpha(accent, 0.35)}`,
      color: "white", position: "relative", overflow: "hidden",
      animation: `${fadeUp} 0.3s ease`,
      transition: "background 0.4s ease",
    }}>
      <Box sx={{ position:"absolute", top:-40, right:-40, width:180, height:180, borderRadius:"50%",
        background:`radial-gradient(circle, ${alpha("#fff", 0.12)} 0%, transparent 70%)`, pointerEvents:"none" }} />
      <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", opacity:0.75, fontSize:"0.65rem" }}>
            {isRequest ? "Request" : "Available Balance"}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={160} height={52} sx={{ bgcolor:alpha("#fff",0.2), borderRadius:1, mt:0.5 }} />
          ) : (
            <Typography variant="h3" sx={{ fontWeight:900, letterSpacing:"-0.03em", lineHeight:1.1, mt:0.5 }}>
            {isRequest ? "Request" : fmt(balance)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:1 }}>
          <Box sx={{ width:48, height:48, borderRadius:"50%", bgcolor:alpha("#fff",0.15),
            display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(8px)" }}>
            {isRequest ? <RequestIcon sx={{ fontSize:24, color:"white" }} /> : <WalletIcon sx={{ fontSize:24, color:"white" }} />}
          </Box>
          {!isRequest && (
            <IconButton size="small" onClick={onRefresh} sx={{ color:alpha("#fff",0.7), p:0.5, "&:hover":{color:"white",bgcolor:alpha("#fff",0.1)} }}>
              <RefreshIcon sx={{ fontSize:16 }} />
            </IconButton>
          )}
        </Box>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt:2 }}>
        {isRequest ? (
          <>
            <Chip label="📧 Email & SMS delivery" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
            <Chip label="⚡ Instant invoice" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
          </>
        ) : (
          <>
            <Chip label="✓ No send fee" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
            <Chip label="⚡ Instant" size="small" sx={{ height:22, fontSize:"0.65rem", fontWeight:700, bgcolor:alpha("#fff",0.18), color:"white", "& .MuiChip-label":{px:1} }} />
          </>
        )}
      </Stack>
    </Box>
  );
}

// ─── Square Recipient Card ────────────────────────────────────────────────────
function SquareRecipientCard({ r, onRemove, resolve }) {
  const theme = useTheme();
  const accent = r.kind === "internal" ? theme.palette.primary.main : r.kind === "email" ? theme.palette.info.main : theme.palette.success.main;
  const profile = resolve(r);
  const photo = profile.pfphref || r.pfphref || null;
  const name2 = profile.displayName || r.label || "";
  const loading = r.kind === "internal" && (profile.loading ?? false);
  const isCreator = profile.isCreator ?? !!r.creatorUsername;

  return (
    <Box sx={{ position:"relative", animation:`${fadeUp} 0.22s ease` }}>
      <Paper variant="outlined" sx={{
        borderRadius:3, overflow:"hidden", borderColor:alpha(accent,0.3), bgcolor:alpha(accent,0.025),
        aspectRatio:"1/1", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", p:1.5, gap:1,
        cursor:"default", transition:"all 0.18s ease", position:"relative",
        "&:hover":{ borderColor:alpha(accent,0.6), boxShadow:`0 4px 16px ${alpha(accent,0.18)}`, transform:"translateY(-2px)" },
      }}>
        <Box sx={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, ${accent}, ${alpha(accent,0.4)})` }} />
        {loading ? <Skeleton variant="circular" width={52} height={52} /> : (
          <Box sx={{ position:"relative" }}>
            <Box sx={{ position:"absolute", inset:-3, borderRadius:"50%", background:`linear-gradient(135deg, ${accent}, ${alpha(accent,0.4)})` }} />
            <Avatar src={photo || undefined} sx={{ width:52, height:52, bgcolor:alpha(accent,0.15), color:accent, fontWeight:800, fontSize:"1.1rem", position:"relative", border:"3px solid white" }}>
              {!photo && name2[0]?.toUpperCase()}
            </Avatar>
            {r.kind === "internal" && (
              <CheckCircleIcon sx={{ position:"absolute", bottom:-2, right:-2, fontSize:14, color:accent, bgcolor:"white", borderRadius:"50%", zIndex:2 }} />
            )}
          </Box>
        )}
        {loading ? <Skeleton width={64} height={12} sx={{ borderRadius:1 }} /> : (
          <Typography variant="caption" sx={{ fontWeight:700, fontSize:"0.78rem", textAlign:"center", lineHeight:1.2, color:"text.primary" }} noWrap>{name2}</Typography>
        )}
        <Chip label={r.kind === "internal" ? (isCreator ? "Creator" : "Member") : r.kind === "email" ? "Email" : "SMS"} size="small"
          sx={{ height:16, fontSize:"0.56rem", fontWeight:700, "& .MuiChip-label":{px:0.6}, bgcolor:alpha(accent,0.12), color:accent }} />
      </Paper>
      <IconButton size="small" onClick={() => onRemove(r.id)} sx={{
        position:"absolute", top:-6, right:-6, zIndex:3, width:20, height:20,
        bgcolor:"white", border:"1.5px solid", borderColor:"grey.200",
        boxShadow:"0 2px 6px rgba(0,0,0,0.12)", p:0,
        "&:hover":{ bgcolor:"error.main", borderColor:"error.main", "& svg":{color:"white"} }, transition:"all 0.15s ease",
      }}>
        <CloseIcon sx={{ fontSize:11, color:"text.disabled" }} />
      </IconButton>
    </Box>
  );
}

// ─── WHO STEP ─────────────────────────────────────────────────────────────────
function WhoStep({ recipients, onAdd, onAddManual, onRemove, onCSV, onNext, balance, balLoading, onRefresh, mode }) {
  const theme = useTheme();
  const { fetchProfile, resolve } = useRecipientProfile();
  const isRequest = mode === "request";
  const accent = isRequest ? theme.palette.success.main : theme.palette.primary.main;

  useEffect(() => {
    recipients.forEach((r) => { if (r.kind === "internal") fetchProfile(r); });
  }, [recipients.map((r) => r.id).join(",")]); // eslint-disable-line

  const [q, setQ]       = useState("");
  const [name, setName] = useState("");
  const [modeContact, setModeContact] = useState("sms");

  const { results, isSearching, hasSearched, isDirectContact } = useGlobalUserSearch(q);
  const trimmed = q.trim();
  const isEmail = isValidEmail(trimmed);
  const isPhone = isValidPhone(trimmed);
  const noMatch = hasSearched && results.length === 0 && !isDirectContact;

  const pick = (r) => { onAdd(r); setQ(""); setName(""); };
  const addDirect = (contact) => {
    const ok = onAddManual(name.trim() || trimmed, contact);
    if (ok) { setQ(""); setName(""); }
  };

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      <BalanceHero balance={balance} loading={balLoading} onRefresh={onRefresh} mode={mode} />

      <TextField fullWidth autoFocus value={q}
        onChange={(e) => { setQ(e.target.value); setName(""); }}
        placeholder={recipients.length
          ? (isRequest ? "Add another person to invoice…" : "Add another person…")
          : (isRequest ? "Search who to invoice…" : "Search by name, @username, email, or phone")}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize:18, color: isSearching ? accent : "text.disabled" }} /></InputAdornment>,
          endAdornment: isSearching ? <CircularProgress size={16} /> : null,
          sx: { borderRadius:2.5, fontSize:"0.95rem" },
        }}
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      />

      {results.length > 0 && (
        <Stack spacing={0.75} sx={{ mt:1.25 }}>
          {results.map((r, i) => {
            const profileSlug = r.creator_username || r.creator || null;
            const isCreator   = !!r.creator_username || r.user_type === "creator";
            return (
              <Paper key={r.global_user_id} variant="outlined" onClick={() => pick(r)}
                sx={{ p:1.5, borderRadius:2.5, cursor:"pointer", display:"flex", alignItems:"center", gap:1.5,
                  borderColor:"grey.200", overflow:"hidden", position:"relative", transition:"all 0.18s ease",
                  animation:`${fadeUp} 0.2s ease both`, animationDelay:`${i*0.04}s`,
                  "&:hover":{ borderColor:accent, bgcolor:alpha(accent,0.025), transform:"translateX(3px)", boxShadow:`0 2px 12px ${alpha(accent,0.12)}` },
                  "&::before":{ content:'""', position:"absolute", left:0, top:0, bottom:0, width:3,
                    background:`linear-gradient(180deg, ${accent}, ${alpha(accent, 0.5)})`, borderRadius:"3px 0 0 3px", opacity:0, transition:"opacity 0.15s" },
                  "&:hover::before":{ opacity:1 },
                }}>
                <Box sx={{ position:"relative", flexShrink:0 }}>
                  <Box sx={{ position:"absolute", inset:-2.5, borderRadius:"50%", background:`linear-gradient(135deg, ${accent}, ${alpha(accent, 0.6)})`, zIndex:0 }} />
                  <Avatar src={r.pfphref || undefined} sx={{ width:42, height:42, bgcolor:alpha(accent,0.1), color:accent, fontSize:"0.9rem", fontWeight:700, position:"relative", zIndex:1, border:"2.5px solid white" }}>
                    {!r.pfphref && (r.display_name || r.creator || "?")[0].toUpperCase()}
                  </Avatar>
                </Box>
                <Box sx={{ flex:1, minWidth:0 }}>
                  <Box sx={{ display:"flex", alignItems:"center", gap:0.6, mb:0.2 }}>
                    <Typography variant="body2" sx={{ fontWeight:700, fontSize:"0.9rem" }} noWrap>{r.display_name || r.creator}</Typography>
                    <CheckCircleIcon sx={{ fontSize:13, color:accent, flexShrink:0 }} />
                  </Box>
                  <Box sx={{ display:"flex", alignItems:"center", gap:0.75 }}>
                    {profileSlug && <Typography variant="caption" color="text.secondary" noWrap>@{profileSlug}</Typography>}
                    <Chip label={isCreator ? "Creator" : "User"} size="small" sx={{ height:16, fontSize:"0.58rem", fontWeight:700,
                      bgcolor:isCreator ? alpha(theme.palette.secondary.main,0.1) : alpha(accent,0.08),
                      color:isCreator ? "secondary.main" : accent }} />
                  </Box>
                  {r.email && <Typography variant="caption" color="text.disabled" sx={{ fontSize:"0.68rem" }} noWrap>{r.email}</Typography>}
                </Box>
                <Chip label={isRequest ? "Request" : "Add"} size="small" color={isRequest ? "success" : "primary"} sx={{ fontWeight:700, fontSize:"0.72rem", height:26, borderRadius:1.5 }} />
              </Paper>
            );
          })}
        </Stack>
      )}

      {isEmail && !isPhone && (
        <Paper variant="outlined" sx={{ mt:1.25, p:2, borderRadius:2.5, borderColor:alpha(theme.palette.info.main,0.3), bgcolor:alpha(theme.palette.info.main,0.025) }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:0.75, mb:1 }}>
            <EmailIcon sx={{ fontSize:14, color:"info.main" }} />
            <Typography variant="caption" sx={{ fontWeight:700, color:"info.main" }}>
              {isRequest ? "Email invoice — " : "Email invite — "}{trimmed}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="Their name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex:1, "& .MuiOutlinedInput-root":{borderRadius:2} }} onKeyDown={(e) => e.key==="Enter" && name.trim() && addDirect(trimmed)} />
            <Button variant="contained" size="small" color={isRequest ? "success" : "primary"} disabled={!name.trim()} onClick={() => addDirect(trimmed)} sx={{ borderRadius:2, whiteSpace:"nowrap", fontWeight:700 }}>Add</Button>
          </Stack>
        </Paper>
      )}

      {isPhone && !isEmail && (
        <Paper variant="outlined" sx={{ mt:1.25, p:2, borderRadius:2.5, borderColor:alpha(theme.palette.success.main,0.3), bgcolor:alpha(theme.palette.success.main,0.025) }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:0.75, mb:1 }}>
            <PhoneIcon sx={{ fontSize:14, color:"success.main" }} />
            <Typography variant="caption" sx={{ fontWeight:700, color:"success.main" }}>
              {isRequest ? "SMS invoice — " : "SMS invite — "}{trimmed}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="Their name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex:1, "& .MuiOutlinedInput-root":{borderRadius:2} }} onKeyDown={(e) => e.key==="Enter" && name.trim() && addDirect(trimmed)} />
            <Button variant="contained" color="success" size="small" disabled={!name.trim()} onClick={() => addDirect(trimmed)} sx={{ borderRadius:2, whiteSpace:"nowrap", fontWeight:700 }}>Add</Button>
          </Stack>
        </Paper>
      )}

      {noMatch && !isEmail && !isPhone && (
        <Paper variant="outlined" sx={{ mt:1.25, p:2, borderRadius:2.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display:"block", mb:1.25, fontWeight:500 }}>
            <strong>"{trimmed}"</strong> isn't on Blitz — {isRequest ? "send them an invoice via:" : "invite them:"}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mb:1.25 }}>
            <Chip label="📱 SMS" size="small" variant={modeContact==="sms"?"filled":"outlined"} color="success" onClick={() => setModeContact("sms")} sx={{ cursor:"pointer", fontWeight:700 }} />
            <Chip label="✉️ Email" size="small" variant={modeContact==="email"?"filled":"outlined"} color="info" onClick={() => setModeContact("email")} sx={{ cursor:"pointer", fontWeight:700 }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder={modeContact==="sms" ? "+1 (555) 000-0000" : "their@email.com"} value={name} onChange={(e) => setName(e.target.value)}
              error={name.trim().length > 4 && !(modeContact==="sms" ? isValidPhone(name) : isValidEmail(name))}
              sx={{ flex:1, "& .MuiOutlinedInput-root":{borderRadius:2} }} />
            <Button variant="contained" size="small" sx={{ borderRadius:2, fontWeight:700 }}
              disabled={!(modeContact==="sms" ? isValidPhone(name) : isValidEmail(name))}
              onClick={() => addDirect(name.trim())}>Add</Button>
          </Stack>
        </Paper>
      )}

      {recipients.length > 0 && (
        <Box sx={{ mt:2.5, animation:`${fadeUp} 0.2s ease` }}>
          <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.64rem", display:"block", mb:1.25 }}>
            {isRequest ? "Invoicing" : "Transferring"} ({recipients.length})
          </Typography>
          <Grid container spacing={1.5} sx={{ mb:2.5 }}>
            {recipients.map((r) => (
              <Grid item xs={4} sm={3} key={r.id}>
                <SquareRecipientCard r={r} onRemove={onRemove} resolve={resolve} />
              </Grid>
            ))}
          </Grid>
          <Button variant="contained" fullWidth size="large" onClick={onNext} endIcon={<ArrowForwardIcon />}
            sx={{ height:50, borderRadius:2.5, fontWeight:700, fontSize:"0.95rem",
              background: isRequest
                ? `linear-gradient(135deg,${theme.palette.success.dark},${theme.palette.success.main})`
                : `linear-gradient(135deg,${theme.palette.primary.dark},${theme.palette.primary.main})`,
              boxShadow:`0 4px 14px ${alpha(isRequest ? theme.palette.success.main : theme.palette.primary.main, 0.35)}`,
              "&:hover":{ transform:"translateY(-1px)" }, transition:"all 0.2s ease" }}>
            {isRequest ? "Set invoice amounts →" : "Set amounts →"}
          </Button>
        </Box>
      )}

      {recipients.length === 0 && (
        <>
          <Box sx={{ display:"flex", alignItems:"center", gap:2, my:3 }}>
            <Divider sx={{ flex:1 }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.63rem", whiteSpace:"nowrap" }}>or {isRequest ? "invoice a list" : "transfer a list"}</Typography>
            <Divider sx={{ flex:1 }} />
          </Box>
          <Box onClick={onCSV} sx={{ p:2.5, borderRadius:3, border:"2px dashed", borderColor:alpha(accent,0.2), cursor:"pointer", display:"flex", alignItems:"center", gap:2,
            transition:"all 0.2s ease", "&:hover":{borderColor:accent, bgcolor:alpha(accent,0.025), transform:"translateY(-2px)", boxShadow:`0 4px 16px ${alpha(accent,0.1)}`} }}>
            <Box sx={{ width:44, height:44, borderRadius:2, bgcolor:alpha(accent,0.08), display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <UploadIcon sx={{ fontSize:22, color:accent }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Import a CSV</Typography>
              <Typography variant="caption" color="text.secondary">Name + email/phone + amount — ready in seconds</Typography>
            </Box>
          </Box>
        </>
      )}
      {recipients.length > 0 && (
        <Box onClick={onCSV} sx={{ mt:1.5, p:1.5, borderRadius:2, border:"1px dashed", borderColor:alpha(accent,0.2), cursor:"pointer",
          display:"flex", alignItems:"center", gap:1, "&:hover":{borderColor:accent, bgcolor:alpha(accent,0.02)}, transition:"all 0.15s" }}>
          <CSVIcon sx={{ fontSize:15, color:accent }} />
          <Typography variant="caption" sx={{ fontWeight:700, color:accent }}>Add more via CSV</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── AMOUNT STEP ──────────────────────────────────────────────────────────────
function AmountStep({ recipients, onSetAmount, onRemove, onBack, onNext, balance, mode }) {
  const total  = recipients.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const allSet = recipients.every((r) => parseFloat(r.amount) > 0);
  const isRequest = mode === "request";
  const { fetchProfile, resolve } = useRecipientProfile();

  useEffect(() => {
    recipients.forEach((r) => { if (r.kind === "internal") fetchProfile(r); });
  }, [recipients.map((r) => r.id).join(",")]); // eslint-disable-line

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      {isRequest && (
        <Alert severity="info" icon={<RequestIcon />} sx={{ mb:2, borderRadius:2.5 }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Set invoice amounts</Typography>
          <Typography variant="caption" color="text.secondary">Each person receives an invoice for their amount via email/SMS.</Typography>
        </Alert>
      )}
      <Grid container spacing={1.75}>
        {recipients.map((r, i) => (
          <Grid item xs={12} sm={recipients.length > 1 ? 6 : 12} key={r.id}>
            <RecipientCard recipient={r} amount={r.amount} onSetAmount={onSetAmount} onRemove={onRemove} autoFocus={i === 0} profileData={resolve(r)} />
          </Grid>
        ))}
      </Grid>
      <TransferSummaryBar recipients={recipients} totalAmount={total} balance={balance} allAmountsSet={allSet} onContinue={onNext} mode={mode} />
      <Button variant="text" size="small" onClick={onBack} startIcon={<ArrowBackIcon sx={{ fontSize:15 }} />}
        sx={{ mt:1.5, textTransform:"none", color:"text.secondary", fontWeight:600, fontSize:"0.8rem" }}>
        Back to search
      </Button>
    </Box>
  );
}

// ─── NOTE STEP ────────────────────────────────────────────────────────────────
function NoteStep({ note, onNote, onBack, onNext, isOverBalance, total, balance, mode }) {
  const theme  = useTheme();
  const isRequest = mode === "request";
  const canNext = note.trim().length >= 3;
  const accent = isRequest ? theme.palette.success.main : theme.palette.primary.main;

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      {isRequest ? (
        <Alert severity="success" icon={<RequestIcon />} sx={{ mb:2.5, borderRadius:2.5, border:"1px solid", borderColor:alpha(theme.palette.success.main,0.25) }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Invoicing {fmt(total)}</Typography>
          <Typography variant="caption" color="text.secondary">
            Recipients get a payment link. Once paid, funds land in your Blitz balance.
          </Typography>
        </Alert>
      ) : isOverBalance ? (
        <Alert severity="warning" icon={<CardIcon />} sx={{ mb:2.5, borderRadius:2.5, border:"1px solid", borderColor:alpha(theme.palette.warning.main,0.3) }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Your balance ({fmt(balance)}) won't cover this transfer.</Typography>
          <Typography variant="caption" color="text.secondary">Since the total ({fmt(total)}) exceeds your balance, this will route via PayPal or card.</Typography>
        </Alert>
      ) : (
        <Alert severity="success" icon={<WalletIcon />} sx={{ mb:2.5, borderRadius:2.5, border:"1px solid", borderColor:alpha(theme.palette.success.main,0.25) }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Transferring {fmt(total)} from your Blitz balance — free.</Typography>
          <Typography variant="caption" color="text.secondary">Your balance covers this. No fees, instant.</Typography>
        </Alert>
      )}

      <Box sx={{ p:2.5, borderRadius:3, border:"1px solid", borderColor:alpha(accent,0.2), bgcolor:alpha(accent,0.02), mb:2 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
          <NoteIcon sx={{ fontSize:18, color:accent }} />
          <Typography variant="body2" sx={{ fontWeight:700 }}>{isRequest ? "Request description" : "What's this transfer for?"}</Typography>
          <Chip label="Required" size="small" sx={{ height:18, fontSize:"0.6rem", fontWeight:700, ml:"auto", bgcolor:alpha(accent,0.1), color:accent, "& .MuiChip-label":{px:0.75} }} />
        </Box>
        <TextField fullWidth multiline minRows={2} maxRows={4} autoFocus value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder={isRequest ? "e.g. Content creation - February, Project milestone #2, Invoice #1042…" : "e.g. January brand deal, invoice #1042, project milestone…"}
          helperText={note.trim().length < 3 && note.length > 0 ? "At least 3 characters required" : ""}
          error={note.trim().length < 3 && note.length > 0}
          sx={{ "& .MuiOutlinedInput-root":{borderRadius:2} }}
          onKeyDown={(e) => { if (e.key==="Enter" && !e.shiftKey && canNext) { e.preventDefault(); onNext(); } }}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onBack} sx={{ borderRadius:2.5, minWidth:80, fontWeight:700 }}>Back</Button>
        <Button variant="contained" fullWidth size="large" disabled={!canNext} onClick={onNext} endIcon={<ArrowForwardIcon />}
          color={isRequest ? "success" : "primary"}
          sx={{ height:50, borderRadius:2.5, fontWeight:700, fontSize:"0.95rem",
            boxShadow: canNext ? `0 4px 14px ${alpha(accent,0.35)}` : "none",
            "&:hover":{ transform:canNext?"translateY(-1px)":"none" }, transition:"all 0.2s ease" }}>
          {isRequest ? "Review invoice →" : (isOverBalance ? "Choose payment method →" : "Review & transfer →")}
        </Button>
      </Stack>
    </Box>
  );
}

// ─── PAY STEP (send only) ─────────────────────────────────────────────────────
function PayStep({ total, rail, onRail, feePayer, onFeePayer, onBack, onNext }) {
  const theme = useTheme();
  const calc  = computeT2A(total, rail, feePayer);

  const RAILS = [
    { id:"paypal", label:"PayPal",      sub:"2.9% + $0.30", color:"#003087", icon:<PayPalIcon sx={{ fontSize:22 }} /> },
    { id:"stripe", label:"Card / Bank", sub:"3.5% + $0.30", color:"#635bff", icon:<CardIcon   sx={{ fontSize:22 }} /> },
  ];

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.64rem", display:"block", mb:1 }}>
        Who covers the processing fee?
      </Typography>
      <Stack spacing={0.75} sx={{ mb:2.5 }}>
        {[
          { id:"sender",    label:"I'll cover it",  badge:"Recipient gets full amount",     accent:theme.palette.primary.main },
          { id:"recipient", label:"They cover it",  badge:"Fee deducted from their amount", accent:theme.palette.warning.main },
        ].map((opt) => (
          <Paper key={opt.id} onClick={() => onFeePayer(opt.id)} variant="outlined"
            sx={{ p:1.75, borderRadius:2.5, cursor:"pointer", display:"flex", alignItems:"center", gap:1.5,
              borderColor:feePayer===opt.id ? opt.accent:"grey.200", borderWidth:feePayer===opt.id?2:1,
              bgcolor:feePayer===opt.id ? alpha(opt.accent,0.03):"transparent", transition:"all 0.15s ease", position:"relative", overflow:"hidden",
              "&::before":feePayer===opt.id ? {content:'""',position:"absolute",top:0,left:0,right:0,height:3,background:opt.accent}:{} }}>
            <Box sx={{ width:20,height:20,borderRadius:"50%",border:"2px solid",borderColor:feePayer===opt.id?opt.accent:"grey.400",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              {feePayer===opt.id && <Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:opt.accent }} />}
            </Box>
            <Box sx={{ flex:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700,fontSize:"0.9rem" }}>{opt.label}</Typography>
              <Typography variant="caption" color="text.secondary">{opt.badge}</Typography>
            </Box>
          </Paper>
        ))}
      </Stack>

      <Typography variant="caption" sx={{ fontWeight:700,color:"text.disabled",textTransform:"uppercase",letterSpacing:"0.08em",fontSize:"0.64rem",display:"block",mb:1 }}>Move via</Typography>
      <Stack spacing={1} sx={{ mb:2.5 }}>
        {RAILS.map((r) => (
          <Paper key={r.id} onClick={() => onRail(r.id)} variant="outlined"
            sx={{ p:2,borderRadius:2.5,cursor:"pointer",display:"flex",alignItems:"center",gap:2,
              borderColor:rail===r.id?r.color:"grey.200",borderWidth:rail===r.id?2:1,
              bgcolor:rail===r.id?alpha(r.color,0.04):"transparent",transition:"all 0.15s ease",position:"relative",overflow:"hidden",
              "&:hover":{borderColor:r.color,boxShadow:`0 2px 12px ${alpha(r.color,0.12)}`},
              "&::before":rail===r.id?{content:'""',position:"absolute",top:0,left:0,right:0,height:3,background:r.color}:{} }}>
            <Box sx={{ width:42,height:42,borderRadius:2,bgcolor:alpha(r.color,rail===r.id?0.12:0.06),display:"flex",alignItems:"center",justifyContent:"center",color:r.color,flexShrink:0 }}>{r.icon}</Box>
            <Box sx={{ flex:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700,fontSize:"0.9rem" }}>{r.label}</Typography>
              <Typography variant="caption" color="text.secondary">{r.sub} processing fee</Typography>
            </Box>
            {rail===r.id && <CheckCircleIcon sx={{ color:r.color,fontSize:20,flexShrink:0 }} />}
          </Paper>
        ))}
      </Stack>

      <Paper sx={{ p:2,borderRadius:2.5,border:"1px solid",borderColor:alpha(theme.palette.primary.main,0.15),bgcolor:alpha(theme.palette.primary.main,0.025),mb:2.5 }}>
        <Typography variant="caption" sx={{ fontWeight:700,color:"text.disabled",textTransform:"uppercase",letterSpacing:"0.07em",fontSize:"0.63rem",display:"block",mb:1.25 }}>Summary</Typography>
        <Stack spacing={0.75}>
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" color="text.secondary">Agreed amount</Typography><Typography variant="body2" sx={{ fontWeight:700 }}>{fmt(total)}</Typography></Box>
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" color="text.secondary">Fee ({feePayer==="sender"?"you pay":"they pay"})</Typography><Typography variant="body2" sx={{ fontWeight:700,color:"error.main" }}>−{fmt(calc.fee)}</Typography></Box>
          <Divider sx={{ my:0.5 }} />
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" sx={{ fontWeight:700 }}>They receive</Typography><Typography variant="body2" sx={{ fontWeight:700,color:"success.main",fontSize:"0.95rem" }}>{fmt(feePayer==="sender"?total:calc.recipientReceives)}</Typography></Box>
          <Box sx={{ display:"flex",justifyContent:"space-between" }}><Typography variant="body2" sx={{ fontWeight:700 }}>You pay</Typography><Typography variant="body2" sx={{ fontWeight:800,color:"primary.main",fontSize:"1rem" }}>{fmt(calc.senderPays)}</Typography></Box>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onBack} sx={{ borderRadius:2.5,minWidth:80,fontWeight:700 }}>Back</Button>
        <Button variant="contained" fullWidth size="large" onClick={onNext} endIcon={<ArrowForwardIcon />}
          sx={{ height:50,borderRadius:2.5,fontWeight:700,fontSize:"0.95rem",
            background:`linear-gradient(135deg,${theme.palette.primary.dark},${theme.palette.primary.main})`,
            boxShadow:`0 4px 14px ${alpha(theme.palette.primary.main,0.35)}`,
            "&:hover":{transform:"translateY(-1px)"},transition:"all 0.2s ease" }}>
          Review & confirm transfer
        </Button>
      </Stack>
    </Box>
  );
}

// ─── REQUEST CONFIRM SCREEN ───────────────────────────────────────────────────
function RequestConfirmScreen({ recipients, totalAmount, description, isLoading, requires2FA, twoFAProps, onBack, onConfirm }) {
  const theme = useTheme();
  const accent = theme.palette.success.main;

  return (
    <Box sx={{ animation:`${fadeUp} 0.25s ease` }}>
      <Alert severity="success" icon={<RequestIcon />} sx={{ mb:2.5, borderRadius:2.5 }}>
        <Typography variant="body2" sx={{ fontWeight:700, mb:0.25 }}>Ready to send {recipients.length} invoice{recipients.length !== 1 ? "s" : ""}</Typography>
        <Typography variant="caption" color="text.secondary">
          Each recipient gets an email and/or SMS with a secure payment link. Funds land in your Blitz balance once paid.
        </Typography>
      </Alert>

      <Paper variant="outlined" sx={{ borderRadius:3, overflow:"hidden", mb:2.5 }}>
        <Box sx={{ px:2.5, py:1.5, bgcolor:alpha(accent,0.06), borderBottom:"1px solid", borderColor:alpha(accent,0.15) }}>
          <Typography variant="caption" sx={{ fontWeight:700, color:accent, textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.63rem" }}>
            Request Summary
          </Typography>
        </Box>
        <Stack divider={<Divider />}>
          {recipients.map((r) => (
            <Box key={r.id} sx={{ px:2.5, py:1.5, display:"flex", alignItems:"center", gap:1.5 }}>
              <Avatar sx={{ width:32, height:32, bgcolor:alpha(accent,0.12), color:accent, fontSize:"0.8rem", fontWeight:700 }}>
                {(r.label || "?")[0].toUpperCase()}
              </Avatar>
              <Box sx={{ flex:1, minWidth:0 }}>
                <Typography variant="body2" sx={{ fontWeight:700, fontSize:"0.88rem" }} noWrap>{r.label}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {r.kind === "email" ? r.email : r.kind === "phone" ? r.phoneNumber : `@${r.creatorUsername || r.globalUserId}`}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight:800, color:accent, fontVariantNumeric:"tabular-nums" }}>
                {fmt(r.amount)}
              </Typography>
            </Box>
          ))}
          <Box sx={{ px:2.5, py:1.5, display:"flex", justifyContent:"space-between", bgcolor:alpha(accent,0.03) }}>
            <Typography variant="body2" sx={{ fontWeight:700 }}>Total Requested</Typography>
            <Typography variant="body2" sx={{ fontWeight:800, color:accent, fontSize:"1rem" }}>{fmt(totalAmount)}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius:3, p:2, mb:2.5 }}>
        <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", fontSize:"0.62rem", textTransform:"uppercase", letterSpacing:"0.07em", display:"block", mb:0.5 }}>Description</Typography>
        <Typography variant="body2" sx={{ fontWeight:500, color:"text.primary" }}>{description}</Typography>
      </Paper>

      {requires2FA && (
        <Paper variant="outlined" sx={{ borderRadius:3, p:2.5, mb:2.5, borderColor:alpha(accent,0.3), bgcolor:alpha(accent,0.02) }}>
          <Typography variant="body2" sx={{ fontWeight:700, mb:1.5 }}>Verify your identity</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display:"block", mb:1.5 }}>
            This request total exceeds $500. Enter the code sent to your email.
          </Typography>
          <TextField fullWidth size="small" placeholder="6-digit code"
            value={twoFAProps.otpCode || ""}
            onChange={(e) => twoFAProps.setOtpCode(e.target.value)}
            error={!!twoFAProps.otpError}
            helperText={twoFAProps.otpError}
            inputProps={{ maxLength:6 }}
            sx={{ "& .MuiOutlinedInput-root":{borderRadius:2, fontFamily:"monospace", letterSpacing:"0.25em"} }}
          />
          {!twoFAProps.otpSent && (
            <Button size="small" onClick={() => twoFAProps.sendOtp("email", totalAmount)} sx={{ mt:1, color:accent, fontWeight:700 }}>
              Resend code
            </Button>
          )}
        </Paper>
      )}

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onBack} sx={{ borderRadius:2.5, minWidth:80, fontWeight:700 }}>Back</Button>
        <Button variant="contained" color="success" fullWidth size="large"
          disabled={isLoading || (requires2FA && (twoFAProps.otpCode || "").length < 6)}
          onClick={onConfirm}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RequestIcon />}
          sx={{ height:50, borderRadius:2.5, fontWeight:700, fontSize:"0.95rem",
            boxShadow:`0 4px 14px ${alpha(accent,0.35)}`,
            "&:hover":{ transform:"translateY(-1px)" }, transition:"all 0.2s ease" }}>
          {isLoading ? "Sending invoices…" : `Send ${recipients.length} Request${recipients.length !== 1 ? "s" : ""}`}
        </Button>
      </Stack>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════════
export default function InternalTransfer() {
  const theme       = useTheme();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const queryClient = useQueryClient();

  // ─── mode: "pay" | "request" ───────────────────────────────────────────────
  const [mode,     setMode]     = useState("pay");
  const [step,     setStep]     = useState(S.WHO);
  const [note,     setNote]     = useState("");
  const [csvOpen,  setCsvOpen]  = useState(false);
  const [rail,     setRail]     = useState("paypal");
  const [feePayer, setFeePayer] = useState("sender");

  const { recipients, addFromSearch, addManual, addFromCSV, remove, setAmount, clear } = useRecipients();
  const twoFA = use2FA(currentUser);

  const { data: balData, isLoading: balLoading, refetch: refetchBal } = useQuery(
    ["myBalance"], () => client.balanceLedger.fetchMyBalance(),
    { enabled: !!currentUser?.id, staleTime: 30000, refetchOnWindowFocus: false }
  );
  const { data: histData, isLoading: histLoading } = useQuery(
    ["balanceHistory", currentUser?.id], () => client.balanceLedger.fetch(currentUser?.id),
    { enabled: !!currentUser?.id, refetchOnWindowFocus: false,
      select: (d) => (d || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) }
  );

  const balance  = parseFloat(balData?.balance || 0);
  const total    = recipients.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const allSet   = recipients.length > 0 && recipients.every((r) => parseFloat(r.amount) > 0);
  const overBal  = total > balance;
  const calc     = computeT2A(total, rail, feePayer);
  const isRequest = mode === "request";
  const needs2FA = isRequest ? (total >= 500) : (overBal ? calc.senderPays >= 500 : total >= 500);
  const inFlow   = step !== S.WHO;

  const resetAll = () => {
    clear(); setNote(""); setStep(S.WHO); twoFA.reset();
    setRail("paypal"); setFeePayer("sender");
  };

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    resetAll();
    setMode(newMode);
  };

  // ─── SEND mutation (v6 unchanged) ─────────────────────────────────────────
  const sendMutation = useMutation(
    (payload) => {
      const fn = overBal ? client.balanceLedger.t2aGift : client.balanceLedger.t2aBatchTransfer;
      return fn(payload);
    },
    {
      onSuccess: (data) => {
        if (overBal) {
          const redirectUrl = data?.approval_url || data?.url;
          if (redirectUrl) {
            toast.info("Redirecting to payment…");
            setTimeout(() => { window.location.href = redirectUrl; }, 400);
            return;
          }
          toast.error("Transfer initiation failed — please try again.");
          return;
        }
        toast.success("Transfer complete! 🎉");
        resetAll();
        queryClient.invalidateQueries(["myBalance"]);
        queryClient.invalidateQueries(["balanceHistory"]);
        refetchBal();
      },
      onError: (err) => {
        const d = err.response?.data;
        if (d?.["2fa_required"]) {
          if (!twoFA.otpSent) {
            const triggerAmount = overBal ? calc.senderPays : total;
            twoFA.sendOtp("email", triggerAmount);
          }
          return;
        }
        toast.error("Transfer failed", { description: d?.error || d?.message });
        if (d?.otp_invalid) { twoFA.setOtpError("Invalid or expired code."); twoFA.setOtpCode(""); }
      },
    }
  );

  // ─── REQUEST mutation ──────────────────────────────────────────────────────
  const requestMutation = useMutation(
    (payload) => client.balanceLedger.requestTransfer(payload),
    {
      onSuccess: () => {
        toast.success(`${recipients.length} invoice${recipients.length !== 1 ? "s" : ""} sent! 📧`);
        resetAll();
        queryClient.invalidateQueries(["balanceHistory"]);
      },
      onError: (err) => {
        const d = err.response?.data;
        if (d?.["2fa_required"]) {
          if (!twoFA.otpSent) twoFA.sendOtp("email", total);
          return;
        }
        toast.error("Failed to send invoices", { description: d?.error || d?.message });
        if (d?.otp_invalid) { twoFA.setOtpError("Invalid or expired code."); twoFA.setOtpCode(""); }
      },
    }
  );

  // ─── SEND confirm ──────────────────────────────────────────────────────────
  const handleSendConfirm = () => {
    if (!balData?.user?.id) { toast.error("Balance data not loaded"); return; }

    const transfers = recipients.map((r) => {
      const agreed = parseFloat(r.amount) || 0;
      if (overBal) {
        const share = total > 0 ? (agreed / total) * calc.senderPays : 0;
        return buildTransferItem(r, +share.toFixed(2), agreed, +((share - agreed).toFixed(2)), feePayer, rail);
      }
      return {
        type:   r.kind === "internal" ? "internal" : r.kind === "email" ? "email" : "phone",
        amount: r.amount,
        ...(r.kind === "internal" ? { recipient_global_user_id: r.globalUserId } : {}),
        ...(r.kind === "email"    ? { recipient_email: r.email }                  : {}),
        ...(r.kind === "phone"    ? { phone_number: r.phoneNumber }                : {}),
        metadata: { t2a:true, agreed_amount:r.amount, fee_amount:"0.00", fee_payer:"sender" },
      };
    });

    sendMutation.mutate({
      sender_id:      balData.user.id,
      description:    note.trim() || "transfer",
      otp_code:       needs2FA ? twoFA.otpCode   : undefined,
      otp_method:     needs2FA ? twoFA.otpMethod : undefined,
      payment_origin: overBal ? "pay_now" : "balance",
      payment_rail:   overBal ? rail       : undefined,
      fee_payer:      overBal ? feePayer   : undefined,
      transfers,
    });
  };

  // ─── REQUEST confirm ───────────────────────────────────────────────────────
  const handleRequestConfirm = () => {
    if (!balData?.user?.id) { toast.error("Balance data not loaded"); return; }

    const invoices = recipients.map((r) => ({
      type:   r.kind === "internal" ? "internal" : r.kind === "email" ? "email" : "phone",
      amount: r.amount,
      label:  r.label,
      ...(r.kind === "internal" ? { recipient_global_user_id: r.globalUserId } : {}),
      ...(r.kind === "email"    ? { recipient_email: r.email }                  : {}),
      ...(r.kind === "phone"    ? { phone_number: r.phoneNumber }                : {}),
    }));

    requestMutation.mutate({
      requester_id:   balData.user.id,
      description:    note.trim() || "Invoice",
      otp_code:       needs2FA ? twoFA.otpCode   : undefined,
      otp_method:     needs2FA ? twoFA.otpMethod : undefined,
      invoices,
    });
  };

  // ─── Step meta ─────────────────────────────────────────────────────────────
  const STEP_META = {
    [S.WHO]:     { label: isRequest ? "Who are you invoicing?"  : "Who are you transferring to?", back: null },
    [S.AMOUNT]:  { label: isRequest ? "Invoice amounts"          : "How much?",                    back: S.WHO },
    [S.NOTE]:    { label: isRequest ? "Invoice description"      : "Add a note",                   back: S.AMOUNT },
    [S.PAY]:     { label: "Wallet method",                                                         back: S.NOTE },
    [S.CONFIRM]: { label: isRequest ? "Review invoices"          : "Review transfer",               back: isRequest ? S.NOTE : (overBal ? S.PAY : S.NOTE) },
  };
  const meta = STEP_META[step];

  const STEPS_ORDER = isRequest
    ? [S.WHO, S.AMOUNT, S.NOTE, S.CONFIRM]
    : [S.WHO, S.AMOUNT, S.NOTE, ...(overBal ? [S.PAY] : []), S.CONFIRM];
  const currentIdx  = STEPS_ORDER.indexOf(step);

  const stepAccent =
    isRequest ? theme.palette.success.main
    : step === S.WHO     ? theme.palette.primary.main
    : step === S.AMOUNT  ? theme.palette.success.main
    : step === S.NOTE    ? theme.palette.info.main
    : step === S.PAY     ? theme.palette.warning.main
    : theme.palette.info.main;

  const mutation = isRequest ? requestMutation : sendMutation;

  return (
    <Box sx={{ width:"100%", p:{ xs:2, md:3 }, pb:10, position:"relative", minHeight:"100vh" }}>
      <Box sx={{ position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none", zIndex:0 }}>
        <GlobeBackground size={1200} />
      </Box>

      <Box sx={{ maxWidth:680, mx:"auto", position:"relative", zIndex:1 }}>

        {/* Header */}
        <Box sx={{ mb:3, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing:"-0.02em", mb:0.25 }}>Blitz T2</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {isRequest ? "Invoice anyone and get paid." : "Search, set, then transfer."}
            </Typography>
          </Box>
          <Box sx={{ width:48, height:48, borderRadius:"50%",
            background: isRequest
              ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`
              : "linear-gradient(135deg, #8f3030 0%, #7a2423 100%)",
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow: isRequest ? `0 4px 16px ${alpha(theme.palette.success.main,0.3)}` : "0 4px 16px rgba(122,36,35,0.3)",
            transition:"all 0.3s ease",
          }}>
            {isRequest ? <RequestIcon sx={{ fontSize:24, color:"white" }} /> : <SwapIcon sx={{ fontSize:24, color:"white" }} />}
          </Box>
        </Box>

        {/* Mode toggle — only visible when not in flow */}
        {!inFlow && (
          <Box sx={{ mb:2.5, display:"flex", justifyContent:"center", animation:`${fadeIn} 0.2s ease` }}>
            <ModeToggle mode={mode} onChange={handleModeChange} disabled={false} />
          </Box>
        )}

        {/* Main card */}
        <Paper elevation={0} sx={{ border:"1px solid", borderColor:"grey.200", borderRadius:3.5, overflow:"hidden", mb:3, position:"relative",
          backdropFilter:"blur(2px)", bgcolor:alpha(theme.palette.background.paper,0.92) }}>
          <Box sx={{ height:4, background:`linear-gradient(90deg,${stepAccent},${alpha(stepAccent,0.4)})`, transition:"background 0.4s ease" }} />

          <Box sx={{ px:3, py:2, borderBottom:"1px solid", borderColor:"grey.100",
            display:"flex", alignItems:"center", gap:1.5,
            bgcolor:alpha(stepAccent,0.025), transition:"background 0.4s ease" }}>
            {meta.back && (
              <IconButton size="small" onClick={() => setStep(meta.back)} sx={{ color:"text.secondary", "&:hover":{color:"text.primary"} }}>
                <ArrowBackIcon sx={{ fontSize:18 }} />
              </IconButton>
            )}
            <Box sx={{ flex:1 }}>
              {step === S.WHO ? (
                <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                  <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.62rem" }}>
                    Blitz T2
                  </Typography>
                  {/* Inline mode indicator in header when in WHO step */}
                  <Chip
                    label={isRequest ? "Invoice" : "Transfer"}
                    size="small"
                    color={isRequest ? "success" : "primary"}
                    icon={isRequest ? <RequestIcon sx={{ fontSize:"11px !important" }} /> : <SendIcon sx={{ fontSize:"11px !important" }} />}
                    sx={{ height:18, fontSize:"0.58rem", fontWeight:700, "& .MuiChip-label":{px:0.75} }}
                  />
                </Box>
              ) : (
                <>
                  <Typography variant="caption" sx={{ fontWeight:700, color:"text.disabled", textTransform:"uppercase", letterSpacing:"0.08em", fontSize:"0.62rem" }}>
                    Step {currentIdx + 1} of {STEPS_ORDER.length}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight:700, lineHeight:1.3 }}>{meta.label}</Typography>
                </>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {STEPS_ORDER.map((s, i) => (
                <Box key={s} sx={{ width:i===currentIdx?20:7, height:7, borderRadius:99,
                  bgcolor:i<=currentIdx?stepAccent:alpha(stepAccent,0.2), transition:"all 0.3s ease" }} />
              ))}
            </Stack>
          </Box>

          <Box sx={{ p:3 }}>
            <Fade in key={`${mode}-${step}`}>
              <Box>
                {step === S.WHO && (
                  <WhoStep recipients={recipients} onAdd={addFromSearch}
                    onAddManual={(label, contact) => addManual(label, contact, "")}
                    onRemove={remove} onCSV={() => setCsvOpen(true)}
                    onNext={() => setStep(S.AMOUNT)}
                    balance={balance} balLoading={balLoading} onRefresh={refetchBal} mode={mode} />
                )}
                {step === S.AMOUNT && (
                  <AmountStep recipients={recipients} onSetAmount={setAmount} onRemove={remove}
                    balance={balance} onBack={() => setStep(S.WHO)} mode={mode}
                    onNext={() => { if (!allSet) return; setStep(S.NOTE); }} />
                )}
                {step === S.NOTE && (
                  <NoteStep note={note} onNote={setNote} isOverBalance={!isRequest && overBal}
                    total={total} balance={balance} mode={mode}
                    onBack={() => setStep(S.AMOUNT)}
                    onNext={() => {
                      if (isRequest) {
                        if (needs2FA && !twoFA.otpSent) twoFA.sendOtp("email", total);
                        setStep(S.CONFIRM);
                      } else {
                        if (overBal) setStep(S.PAY);
                        else {
                          if (needs2FA && !twoFA.otpSent) twoFA.sendOtp("email", total);
                          setStep(S.CONFIRM);
                        }
                      }
                    }} />
                )}
                {step === S.PAY && !isRequest && (
                  <PayStep total={total} rail={rail} onRail={setRail}
                    feePayer={feePayer} onFeePayer={setFeePayer}
                    onBack={() => setStep(S.NOTE)}
                    onNext={() => {
                      if (needs2FA && !twoFA.otpSent) twoFA.sendOtp("email", calc.senderPays);
                      setStep(S.CONFIRM);
                    }} />
                )}
                {step === S.CONFIRM && !isRequest && (
                  <ConfirmScreen recipients={recipients} totalAmount={total} description={note}
                    origin={overBal ? "pay_now" : "balance"} rail={rail} feePayer={feePayer}
                    calc={calc} balance={balance} isLoading={sendMutation.isLoading}
                    requires2FA={needs2FA} twoFAProps={twoFA}
                    onBack={() => setStep(overBal ? S.PAY : S.NOTE)} onConfirm={handleSendConfirm} />
                )}
                {step === S.CONFIRM && isRequest && (
                  <RequestConfirmScreen recipients={recipients} totalAmount={total} description={note}
                    isLoading={requestMutation.isLoading}
                    requires2FA={needs2FA} twoFAProps={twoFA}
                    onBack={() => setStep(S.NOTE)} onConfirm={handleRequestConfirm} />
                )}
              </Box>
            </Fade>
          </Box>
        </Paper>

        {/* Recent transactions */}
        {!inFlow && (
          <Box sx={{ animation:`${fadeIn} 0.3s ease` }}>
            <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:"text.secondary" }}>Recent</Typography>
              {histData?.length > 0 && <Typography variant="caption" color="text.disabled">{histData.length} transactions</Typography>}
            </Box>
            {histLoading ? (
              <Box sx={{ textAlign:"center", py:4 }}><CircularProgress size={24} /></Box>
            ) : !histData?.length ? (
              <Box sx={{ textAlign:"center", py:5, px:3 }}>
                <Box sx={{ width:48, height:48, borderRadius:2.5, mx:"auto", mb:1.5, bgcolor:"grey.100", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <SwapIcon sx={{ color:"text.disabled", fontSize:24 }} />
                </Box>
                <Typography variant="body2" color="text.disabled" fontWeight={500}>No activity yet</Typography>
                <Typography variant="caption" color="text.disabled">Your transfers and invoices will appear here.</Typography>
              </Box>
            ) : (
              <Paper elevation={0} sx={{ border:"1px solid", borderColor:"grey.200", borderRadius:3, overflow:"hidden", backdropFilter:"blur(2px)", bgcolor:alpha(theme.palette.background.paper,0.92) }}>
                <Stack>
                  {histData.slice(0, 8).map((tx, i) => {
                    const d   = txDisplay(tx);
                    const amt = Math.abs(parseFloat(tx.change_amount || 0));
                    return (
                      <Box key={tx.id} sx={{ display:"flex", alignItems:"center", gap:1.5, px:2, py:1.5,
                        borderBottom:i < Math.min(histData.length,8)-1?"1px solid":"none", borderColor:"grey.100",
                        transition:"all 0.15s ease", animation:`${fadeUp} 0.2s ease both`, animationDelay:`${i*0.04}s`,
                        "&:hover":{ bgcolor:"grey.50", transform:"translateX(3px)" } }}>
                        <Box sx={{ width:36, height:36, borderRadius:2,
                          bgcolor:d.credit ? alpha(theme.palette.success.main,0.1) : alpha(theme.palette.primary.main,0.08),
                          color:d.credit ? "success.main" : "primary.main", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {d.icon}
                        </Box>
                        <Box sx={{ flex:1, minWidth:0 }}>
                          <Typography variant="body2" sx={{ fontWeight:600, fontSize:"0.85rem" }} noWrap>{d.title}</Typography>
                          <Typography variant="caption" color="text.disabled">{fmtDate(tx.created_at || tx.timestamp)}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight:700, color:d.credit?"success.main":"text.primary", fontVariantNumeric:"tabular-nums", flexShrink:0 }}>
                          {d.credit ? "+" : "−"}${amt.toFixed(2)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      <CSVUploadModal open={csvOpen} onClose={() => setCsvOpen(false)}
        onImport={(rows) => { addFromCSV(rows); setCsvOpen(false); setStep(S.AMOUNT); }} />
    </Box>
  );
}